export type ApprovalStatus =
  | "PENDING"
  | "UNDER_REVIEW"
  | "APPROVED"
  | "REJECTED";

export interface MerchantApproval {
  _id: string;
  name: string;
  legalName: string;
  category: string;
  presence: string;
  centers: number;
  avgAnnualRevenue: number;
  nabhApproved: boolean;
  lender: string;
  status: ApprovalStatus;
  remark: string;
  createdAt: string;
  updatedAt: string;
}

export interface MerchantApprovalCreateData {
  name: string;
  legalName: string;
  category: string;
  presence: string;
  centers: number;
  avgAnnualRevenue: number;
  nabhApproved: boolean;
  lender: string;
}

export interface MerchantApprovalUpdateData {
  status?: ApprovalStatus;
  remark?: string;
}

interface GetMerchantApprovalsParams {
  page: number;
  pageSize: number;
  search?: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  lender?: string;
  status?: ApprovalStatus;
  token?: string | null;
}

interface GetMerchantApprovalsResponse {
  data: MerchantApproval[];
  pagination: {
    page: number;
    pageSize: number;
    totalMatch: number;
    totalPages: number;
  };
}

const getHeaders = (token?: string | null) => ({
  "Content-Type": "application/json",
  token: token || localStorage.getItem("token") || "",
});

const BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/lender-merchant-approval`;

export const fetchMerchantApprovals = async (
  params: GetMerchantApprovalsParams
): Promise<GetMerchantApprovalsResponse> => {
  const queryParams = new URLSearchParams();

  queryParams.append("page", params.page.toString());
  queryParams.append("pageSize", params.pageSize.toString());
  queryParams.append("sortBy", params.sortBy);
  queryParams.append("sortOrder", params.sortOrder);

  if (params.search?.trim()) queryParams.append("search", params.search.trim());
  if (params.lender?.trim()) queryParams.append("lender", params.lender.trim());
  if (params.status) queryParams.append("status", params.status);

  const response = await fetch(`${BASE_URL}?${queryParams.toString()}`, {
    method: "GET",
    headers: getHeaders(params.token),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json?.message || "Failed to fetch merchant approvals");
  }

  // genericRes.successOk may wrap the body in `result`; support both shapes
  const payload = json?.result ?? json;

  return {
    data: Array.isArray(payload?.data) ? payload.data : [],
    pagination: payload?.pagination ?? {
      page: params.page,
      pageSize: params.pageSize,
      totalMatch: 0,
      totalPages: 0,
    },
  };
};

export const createMerchantApproval = async (
  data: MerchantApprovalCreateData
): Promise<MerchantApproval> => {
  const response = await fetch(BASE_URL, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json?.message || "Failed to create merchant approval");
  }

  return (json?.result?.data ?? json?.data) as MerchantApproval;
};

export const updateMerchantApproval = async (
  id: string,
  data: MerchantApprovalUpdateData,
  token?: string | null
): Promise<MerchantApproval> => {
  const response = await fetch(`${BASE_URL}/${id}`, {
    method: "PATCH",
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json?.message || "Failed to update merchant approval");
  }

  return (json?.result?.data ?? json?.data) as MerchantApproval;
};